import { currentLocale } from '@/lib/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Mail, ShieldCheck, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { OnboardingPipeline, type AltaPipeline } from './OnboardingPipeline';

export interface OnboardingRow {
  rowNumber: number;
  id_alta: string;
  fecha_ingreso: string;
  col_c: string;
  nombre: string;
  /** Columna "Correo Personal" de la planilla (resuelta por header en getAllAltas). */
  correoPersonal?: string;
  clapbase: string;
  correo_winclap: string;
  pais: string;
  col_i: string;
  col_j: string;
  col_k: string;
  col_l: string;
  area: string;
  col_n: string;
  col_o: string;
  col_p: string;
  password: string;
  pipeline: AltaPipeline | null;
}

const fmtDate = (iso: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' });
};

export function OnboardingDetailDialog({
  alta,
  onClose,
  onResendEmail,
  onAddToGroup,
  actionPending,
}: {
  alta: OnboardingRow | null;
  onClose: () => void;
  onResendEmail: (id_alta: string, tipo: 'bienvenida' | 'ev') => void;
  onAddToGroup: (id_alta: string) => void;
  actionPending: boolean;
}) {
  const { t } = useTranslation();
  if (!alta) return null;

  const p = alta.pipeline;
  const tp = (k: string) => t(`users.onboarding_page.${k}`);

  const fields: [string, string][] = [
    [tp('table.id'), alta.id_alta],
    [tp('table.join_date'), alta.fecha_ingreso],
    // col_c es la columna "Año" desde el cambio de layout: el correo personal viene en `correoPersonal`
    [tp('table.personal_email'), alta.correoPersonal || ''],
    [tp('table.winclap_email'), alta.correo_winclap],
    [tp('table.country'), alta.pais],
    [tp('table.community'), alta.col_i],
    [tp('table.role'), alta.col_j],
    [tp('table.impact_level'), alta.col_k],
    [tp('table.team'), alta.col_l],
    [tp('table.area'), alta.area],
    [tp('table.people_manager'), alta.col_n],
    [tp('table.contract_type'), alta.col_o],
    [tp('table.house'), alta.col_p],
  ];

  const pipelineDetail: [string, string | null][] = p
    ? [
        [tp('detail.processed_at'), fmtDate(p.fecha_procesado)],
        [tp('detail.welcome_email'), p.welcome_email],
        [tp('detail.ev_email'), p.ev_email ? `${p.ev_email}${p.ev_email_sent_at ? ` (${fmtDate(p.ev_email_sent_at)})` : ''}` : null],
        [tp('detail.security_group'), p.security_group ? `${p.security_group}${p.security_group_at ? ` (${fmtDate(p.security_group_at)})` : ''}` : null],
        [tp('detail.abm'), p.abm_assignment],
        [tp('detail.enrollment'), p.enrollment_reminder],
        ['Mail al manager (People)', p.people_manager_mail ?? null],
        ['Mail primeros pasos (People)', p.people_first_steps_mail ?? null],
        ['Mail día de ingreso (People)', p.people_admission_mail ?? null],
        [tp('detail.error'), p.error_mensaje],
      ]
    : [];

  return (
    <Dialog open={!!alta} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {alta.nombre}
            <span className="text-sm font-normal text-muted-foreground font-mono">{alta.correo_winclap || tp('status.pending')}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">{tp('detail.pipeline_title')}</p>
            <OnboardingPipeline pipeline={p} hasCorreo={!!alta.correo_winclap} />
            {pipelineDetail.length > 0 && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                {pipelineDetail.map(
                  ([label, value]) =>
                    value && (
                      <div key={label} className="flex justify-between gap-4 border-b border-border/40 py-1">
                        <span className="text-muted-foreground">{label}</span>
                        <span className={`text-right break-all ${String(value).startsWith('error') ? 'text-red-600 dark:text-red-400' : ''}`}>{value}</span>
                      </div>
                    )
                )}
              </div>
            )}
          </div>

          {alta.correo_winclap && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" disabled={actionPending} onClick={() => onResendEmail(alta.id_alta, 'bienvenida')}>
                <Mail className="mr-2 h-4 w-4" /> {tp('actions.resend_welcome')}
              </Button>
              <Button size="sm" variant="outline" disabled={actionPending} onClick={() => onResendEmail(alta.id_alta, 'ev')}>
                <Send className="mr-2 h-4 w-4" /> {tp('actions.resend_ev')}
              </Button>
              <Button size="sm" variant="outline" disabled={actionPending || p?.security_group?.startsWith('agregado')} onClick={() => onAddToGroup(alta.id_alta)}>
                <ShieldCheck className="mr-2 h-4 w-4" /> {tp('actions.add_group_now')}
              </Button>
            </div>
          )}

          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">{tp('detail.sheet_title')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              {fields.map(
                ([label, value]) =>
                  value && (
                    <div key={label} className="flex justify-between gap-4 border-b border-border/40 py-1">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="text-right break-all">{value}</span>
                    </div>
                  )
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
